/* Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import { generatePath } from "react-router-dom";
import { Cell } from "react-table";
import { Text } from "@nordcloud/gnui";
import {
  ReservationsAndCommitmentsCoverageOfferingType,
  ReservationsAndCommitmentsCoverageServiceUsageType,
  ReservationsProvider,
} from "~/generated/graphql";
import { CustomCell } from "~/components/Tables/Cells";
import { dateFormat } from "~/constants";
import { ROUTES } from "~/routing/routes";
import { convertToPercent, inflect } from "~/tools";
import { useGetAzureServiceLabel } from "../hooks";
import { ExistingLink, getInstanceDetails } from "../utils";
import { BarProgressChart } from "./BarProgressChart";

type Props = {
  showLinks: boolean;
  offeringType: ReservationsAndCommitmentsCoverageOfferingType;
  provider: ReservationsProvider;
  serviceUsageType: ReservationsAndCommitmentsCoverageServiceUsageType;
};

export function ReservationsExistingLinkColumns({
  showLinks,
  offeringType,
  provider,
  serviceUsageType,
}: Props) {
  const azureDetails = useGetAzureServiceLabel(serviceUsageType);
  const isReservedInstance =
    offeringType ===
    ReservationsAndCommitmentsCoverageOfferingType.ReservedInstances;
  const isAzure = provider === ReservationsProvider.Azure;
  const awsDetails = isReservedInstance
    ? "Instance Details"
    : "Savings Plan Details";
  const rows = isAzure ? azureDetails : awsDetails;
  const totalUtilization = [
    {
      Header: () => (
        <Text nowrap size="sm">
          Total Utilization
        </Text>
      ),
      accessor: "totalUtilisation",
      width: "15%",
      disableSortBy: true,
      Cell: ({ row }: Cell<ExistingLink>) => (
        <BarProgressChart
          startLabel="Utilisation"
          benefit={row?.original?.yearlyUtilization?.benefit || 0}
          value={convertToPercent(
            parseFloat(row?.original?.yearlyUtilization?.utilization || "0")
          )}
        />
      ),
    },
  ];

  const expirationDays = [
    {
      Header: () => <Text size="sm">Expiration Date</Text>,
      accessor: "endDate",
      width: "10%",
      disableSortBy: true,
      Cell: ({ row }: Cell<ExistingLink>) => {
        const days = Number(row.original.expirationTimeInDays);

        return (
          <CustomCell
            name={dayjs(row.original.to).format(dateFormat.dayMonthYear)}
            description={`${days} ${inflect("day")(days)} left`}
            css={{ whiteSpace: "nowrap" }}
          />
        );
      },
    },
  ];

  const instanceReservationDetails = [
    {
      Header: () => <Text size="sm">{rows}</Text>,
      accessor: "instance",
      disableSortBy: true,
      width: "15%",
      Cell: ({ row }: Cell<ExistingLink>) => {
        const { details, azureDescription } = getInstanceDetails(
          row.original,
          isAzure,
          isReservedInstance
        );

        return (
          <CustomCell
            name={details}
            description={azureDescription}
            url={
              showLinks
                ? generatePath(ROUTES.resource, {
                    nordcloudId: row.original.reservationId,
                  })
                : undefined
            }
          />
        );
      },
    },
    {
      Header: () => <Text size="sm">Purchase Option</Text>,
      accessor: "type",
      disableSortBy: true,
      width: "15%",
      Cell: ({ row }: Cell<ExistingLink>) => {
        const {
          offeringClass,
          paymentOption,
          billingPlan,
          expirationTimeInYears,
        } = row.original;
        const purchaseOption = offeringClass
          ? `${offeringClass} / ${paymentOption}`
          : `${paymentOption}`;
        const reservationsOption = isAzure
          ? `${billingPlan}`
          : `${purchaseOption}`;
        return (
          <CustomCell
            name={expirationTimeInYears}
            description={reservationsOption}
          />
        );
      },
    },
  ];

  return [
    ...instanceReservationDetails,
    ...totalUtilization,
    ...expirationDays,
  ];
}
